import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Smartphone, Download, Star, MapPin, CreditCard, Bell } from "lucide-react";
import { Button } from "./ui/button";

const features = [
  {
    icon: MapPin,
    title: "Find Stations",
    description: "Locate nearby charging stations with real-time availability",
  },
  {
    icon: CreditCard,
    title: "Easy Payments",
    description: "Pay seamlessly via UPI, cards, or MILE PLUS wallet",
  },
  {
    icon: Bell,
    title: "Smart Alerts",
    description: "Get notified when your charge is complete",
  },
];

const AppDownload = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section
      className="py-24 md:py-32 bg-background relative overflow-hidden"
      data-testid="app-download-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary font-medium text-sm uppercase tracking-wider">
              Mobile App
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
              CHARGE SMARTER
              <br />
              <span className="text-primary">WITH MILE PLUS APP</span>
            </h2>
            <p className="text-foreground-muted text-lg mt-6 max-w-lg">
              Download the MILE PLUS app for a seamless charging experience. Find
              stations, track your charges, and pay - all in one place.
            </p>

            {/* App Features */}
            <div className="mt-8 space-y-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{feature.title}</h4>
                    <p className="text-foreground-muted text-sm mt-1">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* App Store Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Button
                className="rounded-xl bg-foreground text-background hover:bg-foreground/90 h-14 px-6 flex items-center gap-3"
                data-testid="app-store-btn"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                </svg>
                <div className="text-left">
                  <p className="text-xs opacity-80">Download on the</p>
                  <p className="font-semibold">App Store</p>
                </div>
              </Button>
              <Button
                className="rounded-xl bg-foreground text-background hover:bg-foreground/90 h-14 px-6 flex items-center gap-3"
                data-testid="play-store-btn"
              >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
                </svg>
                <div className="text-left">
                  <p className="text-xs opacity-80">Get it on</p>
                  <p className="font-semibold">Google Play</p>
                </div>
              </Button>
            </motion.div>

            {/* Rating */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.6 }}
              className="mt-8 flex items-center gap-4"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 text-primary fill-primary"
                  />
                ))}
              </div>
              <span className="text-foreground-muted text-sm">
                4.8 rating • 10,000+ downloads
              </span>
            </motion.div>
          </motion.div>

          {/* Right - Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex justify-center"
          >
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-radial from-primary/20 via-transparent to-transparent" />

            {/* Phone Frame */}
            <div className="relative">
              {/* Phone */}
              <div className="relative w-[280px] md:w-[320px] h-[560px] md:h-[640px] bg-background-paper rounded-[40px] border-4 border-white/10 overflow-hidden shadow-2xl">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-10" />

                {/* Screen Content */}
                <div className="w-full h-full bg-background p-4 pt-10">
                  {/* Status Bar */}
                  <div className="flex justify-between text-xs text-foreground-muted mb-4">
                    <span>9:41</span>
                    <div className="flex gap-1">
                      <span>5G</span>
                      <span>100%</span>
                    </div>
                  </div>

                  {/* App Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-foreground-muted text-sm">Welcome back</p>
                      <p className="font-heading text-lg text-foreground">
                        Nashik Driver
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-black" />
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="glass rounded-2xl p-4 mb-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-foreground-muted text-xs">Wallet Balance</p>
                        <p className="font-heading text-2xl text-primary">Rs 1,250</p>
                      </div>
                      <Button
                        size="sm"
                        className="rounded-full bg-primary text-black text-xs h-8"
                      >
                        Add Money
                      </Button>
                    </div>
                  </div>

                  {/* Nearby Stations */}
                  <div className="mb-4">
                    <p className="text-foreground text-sm font-medium mb-3">
                      Nearby Stations
                    </p>
                    <div className="space-y-3">
                      {[
                        { name: "College Road", dist: "1.2 km", avail: 4 },
                        { name: "Gangapur", dist: "2.5 km", avail: 2 },
                      ].map((station) => (
                        <div
                          key={station.name}
                          className="glass rounded-xl p-3 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <MapPin className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-foreground text-sm">
                                {station.name}
                              </p>
                              <p className="text-foreground-muted text-xs">
                                {station.dist}
                              </p>
                            </div>
                          </div>
                          <span className="text-primary text-xs font-medium">
                            {station.avail} Free
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom Nav */}
                  <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl p-3 flex justify-around">
                    {["Home", "Map", "Wallet", "Profile"].map((item, i) => (
                      <div
                        key={item}
                        className={`text-center ${
                          i === 0 ? "text-primary" : "text-foreground-muted"
                        }`}
                      >
                        <div
                          className={`w-6 h-6 mx-auto mb-1 rounded-full ${
                            i === 0 ? "bg-primary/20" : ""
                          } flex items-center justify-center`}
                        >
                          {i === 0 && <Download className="w-3 h-3" />}
                        </div>
                        <p className="text-[10px]">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-4 -right-8 glass rounded-xl p-3"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <Bell className="w-4 h-4 text-black" />
                  </div>
                  <p className="text-xs text-foreground">Charge Complete!</p>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, delay: 1 }}
                className="absolute -bottom-4 -left-8 glass rounded-xl p-3"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-black" />
                  </div>
                  <p className="text-xs text-foreground">Rs 180 saved!</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AppDownload;
